/**
 * src/help/helpManifest.js
 *
 * AUTHORITATIVE guided-help manifest for the D&D Image and Professional
 * Dashboard pages.
 *
 * This is the single source of truth for every help target. The GuidedHelp
 * engine (./GuidedHelp.js) renders the spotlight, the small yellow context box
 * and the large detailed modal purely from this data. Nothing about the help
 * system is hard-coded in page JSX except the `data-help-id` attributes that
 * mark each real element.
 *
 * Every entry:
 *   id             unique, stable identifier
 *   target         value of the element's data-help-id attribute
 *   shortTitle     heading for the small yellow box (WHAT IS THIS?)
 *   shortText      1 short sentence for the small yellow box
 *   modalTitle     heading for the detailed modal
 *   modalBody      array of short plain-language paragraphs
 *                  (WHAT IS THIS? / WHEN WOULD I USE IT? / WHAT HAPPENS?)
 *   section        grouping used to build the Help topic chooser
 *   order          numeric order within the full tour (assigned automatically)
 *   interactionType 'click' (default) | 'menu-open' | 'submenu-open'
 *   revealVia      optional target id that must be clicked to reveal this one
 *                  (used for the ... menu and its Create Variation submenu)
 *   conditional    true if the element only renders in some account states;
 *                  the engine silently skips conditional targets not in the DOM
 *
 * Copy describes HOW TO USE each control in plain language. It is derived from
 * the real page behaviour, never invented.
 */

// Targets that are meaningful to explain but are NOT normally clickable
// controls (labels, values, status, images, section headers, category pills).
// The engine must not tell the user to "click" these — it offers a Continue
// button instead. Everything else is an interactive control the user clicks.
export const INFORMATIONAL_TARGETS = new Set([
  'header-title', 'header-badge',
  'continue-card', 'continue-thumb',
  'plan-card', 'plan-name', 'plan-status',
  'anim-credits', 'anim-count',
  'credits-card', 'credits-used', 'credits-limit', 'credits-remaining', 'credits-reset', 'bonus-credits',
  'library-card', 'lib-pill-0', 'lib-pill-1', 'lib-pill-2', 'lib-pill-3',
  'recent-card', 'build-image', 'build-name', 'build-date', 'build-status',
  'quicklinks-card',

  // ── Battlemaps informational targets (labels / values / status / previews) ──
  'bm-control-centre',
  'bm-packs-owned', 'bm-creation-credits', 'bm-recent-metric', 'bm-owned-packs',
  'bm-creation-suite',
  'bm-engine-tactical', 'bm-engine-cinematic', 'bm-engine-structures', 'bm-engine-props', 'bm-engine-terrain',
  'bm-video-badge',
  'bm-recent-card', 'bm-recent-empty', 'bm-build-image', 'bm-build-name', 'bm-build-date',
  'bm-quicklinks-card',
]);

// VISINVOKE Security informational (non-clickable) help targets.
['sec-header-title', 'sec-header-badge', 'sec-overview', 'sec-billing-total', 'sec-first-setup', 'sec-endpoint-card', 'sec-protection-status', 'sec-c-managed', 'sec-c-av', 'sec-c-states', 'sec-c-offline', 'sec-c-devices', 'sec-c-llw', 'sec-c-uninstall', 'sec-c-billing', 'sec-c-payment', 'sec-c-privacy'].forEach((id) => INFORMATIONAL_TARGETS.add(id));

export const SECTIONS = [
  { key: 'navigation', label: 'Top Navigation' },
  { key: 'header',     label: 'Page Header' },
  { key: 'continue',   label: 'Continue Your Last Build' },
  { key: 'plan',       label: 'Plan' },
  { key: 'animation',  label: 'Animation Credits' },
  { key: 'credits',    label: 'Credits' },
  { key: 'library',    label: 'Generator Library' },
  { key: 'recent',     label: 'Recent Builds' },
  { key: 'quicklinks', label: 'Quick Links' },
];

// Battlemaps has a different page structure (no plan/animation/credits meters,
// no D&D generator library) so it has its own topic sections. The engine and
// entry points are shared; only the manifest data differs.
export const BATTLEMAPS_SECTIONS = [
  { key: 'header',     label: 'Page Header' },
  { key: 'control',    label: 'Battlemaps Control Centre' },
  { key: 'account',    label: 'Packs & Credits' },
  { key: 'suite',      label: 'Creation Suite' },
  { key: 'recent',     label: 'Recent Builds' },
  { key: 'quicklinks', label: 'Quick Links' },
  { key: 'navigation', label: 'Top Navigation' },
];

export const SECURITY_SECTIONS = [
  { key: 'overview',  label: 'Security Overview' },
  { key: 'setup',     label: 'Getting Started' },
  { key: 'computers', label: 'Your Computers' },
  { key: 'install',   label: 'Installation' },
  { key: 'lifecycle', label: 'Changes & Removal' },
  { key: 'concepts',  label: 'Understanding Your Protection' },
  { key: 'theft',     label: 'If A Laptop Is Lost Or Stolen' },
];

// The sections that build the Help topic chooser for a given page.
export function sectionsFor(pageKey) {
  if (pageKey === 'security') return SECURITY_SECTIONS;
  return pageKey === 'battlemaps' ? BATTLEMAPS_SECTIONS : SECTIONS;
}

// Per-page wording that actually differs between D&D and Professional.
const PAGE = {
  visual: {
    productName:   'D&D Image Generators',
    libraryTitle:  'D&D Image Generator Library',
    browseLabel:   'Browse Generators',
    pills: [
      { id: 'lib-pill-0', shortTitle: 'D&D Generators',
        text: 'The set of fantasy and worldbuilding image generators available in this library.',
        body: ['This pill marks the type of generators in this library: Dungeons & Dragons and other fantasy or game-based image generators.',
               'Use it as a reminder of which library you are in before you browse.',
               'It is a label, not a button — nothing changes when you look at it.'] },
      { id: 'lib-pill-1', shortTitle: 'Full Search',
        text: 'You can search the whole generator library by name or keyword.',
        body: ['This tells you the library supports a full search.',
               'When you open the library with Browse Generators, you can type what you are looking for instead of scrolling.',
               'It helps you jump straight to a specific generator.'] },
      { id: 'lib-pill-2', shortTitle: 'A–Z Filter',
        text: 'The library can be filtered and sorted alphabetically.',
        body: ['This tells you the library can be filtered A to Z.',
               'Use it when you want to browse generators in alphabetical order rather than by category.',
               'It makes a large library easier to move through.'] },
      { id: 'lib-pill-3', shortTitle: 'Depth of Control',
        text: 'Each generator offers thousands of structured selections and dozens of custom inputs.',
        body: ['This describes how much control each generator gives you: up to 4,000+ structured selections and up to 38 custom-configured inputs.',
               'It is here so you know these are deep, precise tools — not one-click presets.',
               'You choose as much or as little detail as you like when you build an image.'] },
    ],
  },
  professional: {
    productName:   'Professional Generators',
    libraryTitle:  'Professional Generator Library',
    browseLabel:   'Browse Professional Generators',
    pills: [
      { id: 'lib-pill-0', shortTitle: 'Architecture',
        text: 'One of the professional categories: architectural and building imagery.',
        body: ['This pill marks the Architecture category inside the Professional library.',
               'Use it when the image you need is a building, interior, or architectural visual.',
               'It is a category label — browsing the library is where you actually open it.'] },
      { id: 'lib-pill-1', shortTitle: 'Branding & Marketing',
        text: 'A professional category for branding, advertising and marketing visuals.',
        body: ['This pill marks the Branding & Marketing category in the Professional library.',
               'Use it when you need brand, campaign, or marketing imagery.',
               'It is a category label; you open it from inside the library.'] },
      { id: 'lib-pill-2', shortTitle: 'Product Photography',
        text: 'A professional category for product and commercial photography styles.',
        body: ['This pill marks the Product Photography category in the Professional library.',
               'Use it when you need clean, commercial product images.',
               'It is a category label; you open it from inside the library.'] },
      { id: 'lib-pill-3', shortTitle: 'Library Size',
        text: 'The professional library spans thousands of generators across many categories.',
        body: ['This tells you the Professional library contains 4,000+ generators across 24 professional categories.',
               'It is here so you know how much is available before you start browsing.',
               'Use Browse Professional Generators to open the full catalogue.'] },
    ],
  },
};

// Category-navigation chapter — short, efficient (spec: keep this concise).
const NAV_TARGETS = [
  { id: 'nav-visual',        target: 'nav-visual',        shortTitle: 'D&D Image',
    text: 'Opens the D&D image generators.',
    body: ['This opens the D&D Image area — fantasy, RPG and game-based image generators.', 'Use it to come back here from anywhere in VISINVOKE.'] },
  { id: 'nav-professional',  target: 'nav-professional',  shortTitle: 'Professional',
    text: 'Opens the professional / commercial generators.',
    body: ['This opens the Professional area — business, marketing and commercial image generators.', 'Use it when your image is for work rather than fantasy.'] },
  { id: 'nav-battlemaps',    target: 'nav-battlemaps',    shortTitle: 'Battlemaps',
    text: 'Opens the tactical battlemap generators.',
    body: ['This opens Battlemaps — tactical maps, terrain and encounter scenes.', 'Use it when you need a map rather than a character or object image.'] },
  { id: 'nav-battletracker', target: 'nav-battletracker', shortTitle: 'Combat PRO',
    text: 'Opens the Combat PRO encounter manager.',
    body: ['This opens Combat PRO — the all-in-one D&D combat and encounter manager.', 'Use it to run initiative, HP and conditions during play.'] },
  { id: 'nav-rpg',           target: 'nav-rpg',           shortTitle: 'RPG Worlds',
    text: 'Opens the RPG Worlds area.',
    body: ['This opens RPG Worlds — persistent solo AI-driven adventures.', 'Use it to explore the worlds and their plans.'] },
  { id: 'nav-security',      target: 'nav-security',      shortTitle: 'Security',
    text: 'Opens the Security services area.',
    body: ['This opens Security — managed protection services for your computer.', 'Use it to review the available protection services.'] },
  { id: 'nav-utilities',     target: 'nav-utilities',     shortTitle: 'Utilities',
    text: 'Opens the Utilities area.',
    body: ['This opens Utilities — Windows, business and IT tools in the VISINVOKE ecosystem.', 'Use it to reach the free and paid utility collections.'] },
];

const GLOBAL_NAV_TARGETS = [
  { id: 'nav-command-center', target: 'nav-command-center', shortTitle: 'Get Command Center',
    text: 'Opens the Desktop Command Center page.',
    body: ['This opens the Desktop Command Center — the desktop companion for VISINVOKE.', 'Use it to learn about or set up the desktop app.'] },
  { id: 'nav-growth', target: 'nav-growth', shortTitle: 'Growth & Pricing',
    text: 'Opens plans, pricing and growth options.',
    body: ['This opens Growth & Pricing — the full view of plans and pricing.', 'Use it when comparing what each membership includes.'] },
  { id: 'nav-generators', target: 'nav-generators', shortTitle: 'Generators',
    text: 'Opens the standalone generators overview.',
    body: ['This opens the generators overview.', 'Use it as a direct shortcut into the generator catalogue.'] },
  { id: 'nav-account', target: 'nav-account', shortTitle: 'Account',
    text: 'Opens your account settings and billing.',
    body: ['This opens your account area — profile, subscription and billing settings.', 'Use it to manage your membership and details.'] },
  { id: 'nav-signout', target: 'nav-signout', shortTitle: 'Sign Out',
    text: 'Signs you out of VISINVOKE.',
    body: ['This signs you out of your VISINVOKE account.', 'Use it when you are finished, especially on a shared computer.'] },
];

// The ... menu on a recent build, and its Create Variation submenu.
const MENU_TARGETS = [
  { id: 'build-menu', target: 'build-menu', section: 'recent', interactionType: 'menu-open',
    shortTitle: 'More Options (⋯)',
    text: 'Opens more ways to refine or reuse this build.',
    body: ['This ⋯ button opens a menu of extra actions for this build.',
           'Use it when you want to refine the image or make a new version instead of continuing as-is.',
           'Opening it here will not change your build — the next steps explain each choice inside it.'] },
  { id: 'menu-subject', target: 'menu-subject', section: 'recent', revealVia: 'build-menu',
    shortTitle: 'Strengthen Subject Focus',
    text: 'Reopens this build with the main subject emphasised.',
    body: ['This reopens the build in the generator with settings aimed at making the main subject clearer and more prominent.',
           'Use it when the subject of your image felt weak or lost in the scene.',
           'It takes you into the generator ready to regenerate — your saved build is not overwritten.'] },
  { id: 'menu-environment', target: 'menu-environment', section: 'recent', revealVia: 'build-menu',
    shortTitle: 'Clarify Environment',
    text: 'Reopens this build with the background/scene emphasised.',
    body: ['This reopens the build focused on clarifying the environment or background around your subject.',
           'Use it when the setting looked muddy or unclear.',
           'It opens the generator ready to regenerate with that emphasis.'] },
  { id: 'menu-state', target: 'menu-state', section: 'recent', revealVia: 'build-menu',
    shortTitle: 'Enforce State',
    text: 'Reopens this build with its described state applied more strictly.',
    body: ['This reopens the build with the pose, action or condition you described applied more strictly.',
           'Use it when the image ignored a detail you asked for.',
           'It opens the generator ready to regenerate with that emphasis.'] },
  { id: 'menu-variation', target: 'menu-variation', section: 'recent', interactionType: 'submenu-open', revealVia: 'build-menu',
    shortTitle: 'Create Variation ▸',
    text: 'Opens variation styles to make a new take on this build.',
    body: ['This opens a small list of variation styles for this build.',
           'Use it when you like the build but want a different take rather than the same image again.',
           'Choosing a style takes you into the generator to create that variation.'] },
  { id: 'var-standard', target: 'var-standard', section: 'recent', revealVia: 'menu-variation',
    shortTitle: 'Standard Variation',
    text: 'Makes a fresh variation while keeping the same overall build.',
    body: ['This creates a new version of the image using your same settings for natural variety.',
           'Use it when you simply want another take on the same idea.',
           'It opens the generator ready to produce the variation.'] },
  { id: 'var-composition', target: 'var-composition', section: 'recent', revealVia: 'menu-variation',
    shortTitle: 'Stronger Composition',
    text: 'A variation aimed at a stronger, more deliberate layout.',
    body: ['This creates a variation that pushes toward a stronger, more deliberate composition.',
           'Use it when the framing or arrangement felt flat.',
           'It opens the generator ready to produce the variation.'] },
  { id: 'var-cinematic', target: 'var-cinematic', section: 'recent', revealVia: 'menu-variation',
    shortTitle: 'More Cinematic',
    text: 'A variation aimed at a more cinematic, dramatic look.',
    body: ['This creates a variation with a more cinematic, dramatic feel.',
           'Use it when you want more mood and atmosphere.',
           'It opens the generator ready to produce the variation.'] },
  { id: 'var-clean', target: 'var-clean', section: 'recent', revealVia: 'menu-variation',
    shortTitle: 'Cleaner Output',
    text: 'A variation aimed at a cleaner, simpler result.',
    body: ['This creates a variation aimed at a cleaner, less cluttered result.',
           'Use it when the previous image felt busy or noisy.',
           'It opens the generator ready to produce the variation.'] },
  { id: 'build-versions', target: 'build-versions', section: 'recent', revealVia: 'build-menu', conditional: true,
    shortTitle: 'View Versions',
    text: 'Shows the saved versions you have made of this build.',
    body: ['This shows the earlier versions you have already generated from this build.',
           'Use it to compare takes and pick one to continue from.',
           'It opens a panel of those versions; it only appears when this build has more than one version.'] },
];

// Quick Links — every row is its own target.
const QUICKLINK_TARGETS = (page) => ([
  { id: 'ql-battlemaps', target: 'ql-explore-battlemaps', shortTitle: 'Explore Battlemaps',
    text: 'Jumps to the Battlemaps area.',
    body: ['A shortcut into Battlemaps — map packs and tactical systems.', 'Use it to switch to map-making without using the top navigation.'] },
  { id: 'ql-combat', target: 'ql-open-combat-system-pro', shortTitle: 'Open Combat System PRO',
    text: 'Jumps to Combat PRO.',
    body: ['A shortcut into Combat PRO to run encounters and manage combat.', 'Use it to jump straight to live combat tools.'] },
  { id: 'ql-manage', target: 'ql-manage-subscription', shortTitle: 'Manage Subscription',
    text: 'Opens your subscription and billing.',
    body: ['A shortcut to manage your subscription — change plan and see billing history.', 'Use it to review or change what you pay for.'] },
  { id: 'ql-plans', target: 'ql-view-plans', shortTitle: 'View Plans',
    text: 'Opens all plans and pricing.',
    body: ['A shortcut to view every plan and its pricing.', 'Use it to compare tiers before upgrading.'] },
  { id: 'ql-browse', target: 'ql-browse-generators', shortTitle: page.browseLabel,
    text: 'Opens the generator library.',
    body: ['A shortcut into the generator library.', 'Use it to start a new image without scrolling to the library card.'] },
  { id: 'ql-workspace', target: 'ql-build-workspace', shortTitle: 'Build Workspace',
    text: 'Opens your saved builds and presets.',
    body: ['A shortcut into your Build Workspace — saved builds, presets, templates and history.', 'Use it to pick up earlier work.'] },
  { id: 'ql-recent', target: 'ql-view-recent-builds', shortTitle: 'View Recent Builds',
    text: 'Opens your latest builds in the workspace.',
    body: ['A shortcut to your most recent builds inside the workspace.', 'Use it to continue your latest generator work.'] },
  { id: 'ql-rewards', target: 'ql-rewards-referrals', shortTitle: 'Rewards & Referrals',
    text: 'Opens rewards and referrals.',
    body: ['A shortcut to Rewards & Referrals — earn credits by sharing VISINVOKE.', 'Use it to invite others and gain credits.'] },
  { id: 'ql-password', target: 'ql-change-password', shortTitle: 'Change Password',
    text: 'Opens the change-password box on this page.',
    body: ['This opens a small box on this page for changing your account password.', 'Use it to update your password without leaving the dashboard.'] },
]);

// Shared finalisation: assign order, resolve short/modal copy and classify each
// target as interactive (user clicks the real control) or informational (a
// value/label explained with a Continue button). Used by every page so the
// classification and copy rules live in exactly one place.
function finalize(rawSteps) {
  return rawSteps.map((step, i) => ({
    ...step,
    interactionType: step.interactionType || 'click',
    conditional: step.conditional || false,
    kind: INFORMATIONAL_TARGETS.has(step.id) ? 'informational' : 'interactive',
    order: i + 1,
    shortTitle: step.shortTitle,
    // the small yellow box uses `text`; the detailed modal uses `body`
    shortText: step.shortText || step.text,
    modalTitle: step.modalTitle || step.shortTitle,
    modalBody: step.modalBody || step.body || [step.shortText || step.text],
  }));
}

// ───────────────────────────────────────────────────────────────────────────
// BATTLEMAPS manifest.
//
// Same definition model, engine and entry points as D&D / Professional — only
// the data differs. Copy describes the real, current Battlemaps page behaviour
// (approved amber redesign + lower Recent Builds / Quick Links section). It
// reuses the shared top-navigation chapter (NAV_TARGETS + GLOBAL_NAV_TARGETS),
// the shared page header hooks (header-title / header-badge) and the stable
// data-help-id hooks already present on the Battlemaps page. Conditional
// targets (owned-packs section, empty state vs. build cards) are silently
// skipped by the engine when not in the DOM.
// ───────────────────────────────────────────────────────────────────────────
function buildBattlemapsManifest() {
  const header = [
    { id: 'header-title', target: 'header-title', section: 'header',
      shortTitle: 'This Page',
      text: 'You are on the Battlemaps page.',
      body: ['This is the Battlemaps page — your home base for making tactical maps, terrain, structures, props and cinematic scenes.',
             'Everything you need to start a new map or return to a recent one is on this page.'] },
    { id: 'header-badge', target: 'header-badge', section: 'header', conditional: true,
      shortTitle: 'Where You Are',
      text: 'A badge confirming you are in Battlemaps.',
      body: ['This badge simply confirms which VISINVOKE area you are looking at right now — Battlemaps.',
             'It is a label, not a button — it helps you stay oriented as you move between areas.'] },
  ];

  const control = [
    { id: 'bm-control-centre', target: 'bm-control-centre', section: 'control',
      shortTitle: 'Battlemaps Control Centre',
      text: 'The five Battlemaps systems share one environment library.',
      body: ['This is your Battlemaps control centre. It explains the core idea of Battlemaps: five creation systems that all share one environment library.',
             'When you buy an environment once, it unlocks across every one of the five systems, permanently.',
             'It is an explanation, not a button — the two buttons beside it are where you buy or compare environments.'] },
    { id: 'bm-header-packs', target: 'bm-header-packs', section: 'control',
      shortTitle: 'Environment Packs',
      text: 'Use this to browse and buy environment packs.',
      body: ['This opens the Environment Packs area.',
             'Use it when you want to add a new environment that unlocks across all five Battlemaps systems.',
             'Clicking it takes you to the packs page; nothing is purchased just by opening it.'] },
    { id: 'bm-header-pricing', target: 'bm-header-pricing', section: 'control',
      shortTitle: 'Pricing',
      text: 'Opens Battlemaps pricing and plans.',
      body: ['This opens the Battlemaps pricing page.',
             'Use it to compare what packs and credits cost before you buy.',
             'Clicking it takes you to pricing; it does not change your account.'] },
  ];

  const account = [
    { id: 'bm-packs-owned', target: 'bm-packs-owned', section: 'account',
      shortTitle: 'Environment Packs Owned',
      text: 'How many environment packs you currently own.',
      body: ['This number is how many Environment Packs you own right now.',
             'Each owned pack unlocks one environment across all five creation systems, permanently.',
             'It is a status value — use the Environment Packs button above to add more.'] },
    { id: 'bm-creation-credits', target: 'bm-creation-credits', section: 'account',
      shortTitle: 'Creation Credits',
      text: 'The credits spent when you generate in Battlemaps.',
      body: ['This is your Creation Credits balance — the currency spent when you generate in Battlemaps.',
             'A cinematic video costs more than a still map, so your balance falls faster when you make video.',
             'It is a status value; you top it up from Pricing or the packs area.'] },
    { id: 'bm-recent-metric', target: 'bm-recent-metric', section: 'account',
      shortTitle: 'Recent Builds Count',
      text: 'How many recent Battlemaps builds you have.',
      body: ['This number is how many recent Battlemaps builds you have.',
             'It matches the Recent Builds section further down the page, where each of those builds is listed.',
             'It is a status value, not a button — scroll to Recent Builds to open any of them.'] },
    { id: 'bm-owned-packs', target: 'bm-owned-packs', section: 'account', conditional: true,
      shortTitle: 'Your Environment Packs',
      text: 'The environments you already own.',
      body: ['This section lists the Environment Packs you already own.',
             'Each one is unlocked across all five creation systems, so you can use it in any of them.',
             'It only appears once you own at least one pack.'] },
    { id: 'bm-packs-browse', target: 'bm-packs-browse', section: 'account', conditional: true,
      shortTitle: 'Browse All Packs',
      text: 'Opens the full environment pack catalogue.',
      body: ['This opens the full Environment Packs catalogue.',
             'Use it to see every environment available, not just the ones you own.',
             'It only appears when you already own at least one pack.'] },
  ];

  const suite = [
    { id: 'bm-creation-suite', target: 'bm-creation-suite', section: 'suite',
      shortTitle: 'Creation Suite',
      text: 'The five systems you use to make Battlemaps content.',
      body: ['This is the Creation Suite — the five systems you use to make Battlemaps content.',
             'Each system is built for a different kind of output; the next steps explain each one.',
             'It is a heading — you open a system from its own Open button.'] },

    { id: 'bm-engine-tactical', target: 'bm-engine-tactical', section: 'suite',
      shortTitle: 'Tactical Battlemaps',
      text: 'Full, grid-aligned battlemaps for virtual tabletops.',
      body: ['Tactical Battlemaps generates fully playable, grid-aligned battlemaps designed for virtual tabletop systems.',
             'Choose it when you need a complete, encounter-ready map to play on.',
             'Its Open button takes you into this system to start building.'] },
    { id: 'bm-engine-tactical-open', target: 'bm-engine-tactical-open', section: 'suite',
      shortTitle: 'Open Tactical Battlemaps',
      text: 'Opens the Tactical Battlemaps system.',
      body: ['This opens the Tactical Battlemaps creation system.',
             'Use it when the map you need is a full, playable tactical battlemap.',
             'Clicking it takes you into that system ready to build.'] },

    { id: 'bm-engine-cinematic', target: 'bm-engine-cinematic', section: 'suite',
      shortTitle: 'Cinematic Scenes',
      text: 'The video-native scene engine — generates moving footage.',
      body: ['Cinematic Scenes is the dedicated video-native scene engine: you compose a scene across layers and generate moving footage of it.',
             'Choose it when you want video, not a still image.',
             'Its Open button takes you into this system to compose a scene.'] },
    { id: 'bm-video-badge', target: 'bm-video-badge', section: 'suite',
      shortTitle: 'Video',
      text: 'Marks the system that produces video instead of stills.',
      body: ['This “Video” badge marks Cinematic Scenes as the one system that generates video rather than still images.',
             'It is here so you can tell at a glance which system produces moving footage.',
             'It is a label, not a button.'] },
    { id: 'bm-engine-cinematic-open', target: 'bm-engine-cinematic-open', section: 'suite',
      shortTitle: 'Open Cinematic Scenes',
      text: 'Opens the Cinematic Scenes system.',
      body: ['This opens the Cinematic Scenes creation system.',
             'Use it when you want to generate a moving, cinematic scene rather than a still map.',
             'Clicking it takes you into that system ready to build.'] },

    { id: 'bm-engine-structures', target: 'bm-engine-structures', section: 'suite',
      shortTitle: 'Structure Assets',
      text: 'Modular buildings and structures for maps.',
      body: ['Structure Assets creates modular buildings and structures for use in tactical battlemaps and encounter design.',
             'Choose it when you need placeable structures rather than a whole map.',
             'Its Open button takes you into this system to start building.'] },
    { id: 'bm-engine-structures-open', target: 'bm-engine-structures-open', section: 'suite',
      shortTitle: 'Open Structure Assets',
      text: 'Opens the Structure Assets system.',
      body: ['This opens the Structure Assets creation system.',
             'Use it to generate modular buildings and structures to place on your maps.',
             'Clicking it takes you into that system ready to build.'] },

    { id: 'bm-engine-props', target: 'bm-engine-props', section: 'suite',
      shortTitle: 'Props & Objects',
      text: 'Detailed props and encounter objects.',
      body: ['Props & Objects generates detailed props and encounter objects for enhancing tactical map gameplay.',
             'Choose it when you need scatter props or encounter objects to dress a map.',
             'Its Open button takes you into this system to start building.'] },
    { id: 'bm-engine-props-open', target: 'bm-engine-props-open', section: 'suite',
      shortTitle: 'Open Props & Objects',
      text: 'Opens the Props & Objects system.',
      body: ['This opens the Props & Objects creation system.',
             'Use it to generate props and objects to place on your maps.',
             'Clicking it takes you into that system ready to build.'] },

    { id: 'bm-engine-terrain', target: 'bm-engine-terrain', section: 'suite',
      shortTitle: 'Terrain Tiles',
      text: 'Seamless terrain tiles for building maps.',
      body: ['Terrain Tiles builds seamless terrain tiles and environmental segments for custom map construction.',
             'Choose it when you want to assemble your own map from tile-based environments.',
             'Its Open button takes you into this system to start building.'] },
    { id: 'bm-engine-terrain-open', target: 'bm-engine-terrain-open', section: 'suite',
      shortTitle: 'Open Terrain Tiles',
      text: 'Opens the Terrain Tiles system.',
      body: ['This opens the Terrain Tiles creation system.',
             'Use it to generate seamless terrain tiles for building your own maps.',
             'Clicking it takes you into that system ready to build.'] },
  ];

  const recent = [
    { id: 'bm-recent-card', target: 'bm-recent-card', section: 'recent',
      shortTitle: 'Recent Builds',
      text: 'Your latest Battlemaps generations.',
      body: ['This section shows your most recent Battlemaps generations.',
             'Use it to return to a build without searching your workspace.',
             'Battlemaps builds are kept separate from your D&D and Professional image builds.'] },
    { id: 'bm-workspace', target: 'bm-workspace', section: 'recent',
      shortTitle: 'Open Workspace',
      text: 'Opens your full Battlemaps workspace.',
      body: ['This opens your workspace, where your saved Battlemaps builds live.',
             'Use it when the build you want is not among the most recent few shown here.',
             'Clicking it takes you to the workspace.'] },
    { id: 'bm-recent-empty', target: 'bm-recent-empty', section: 'recent', conditional: true,
      shortTitle: 'No Builds Yet',
      text: 'Shown when you have not generated any Battlemaps yet.',
      body: ['This message appears when you have not generated any Battlemaps builds yet.',
             'Open one of the creation systems above to generate your first map.',
             'Once you do, your completed builds appear here instead of this message.'] },
    { id: 'bm-build-image', target: 'bm-build-image', section: 'recent', conditional: true,
      shortTitle: 'Build Preview',
      text: 'A preview of a recent Battlemaps build.',
      body: ['This is a preview image of one of your recent Battlemaps builds.',
             'It helps you recognise the build at a glance.',
             'It only appears when you have at least one completed build.'] },
    { id: 'bm-build-name', target: 'bm-build-name', section: 'recent', conditional: true,
      shortTitle: 'Build Name',
      text: 'The name of a recent build.',
      body: ['This is the name of a recent Battlemaps build.',
             'It helps you tell your builds apart.',
             'It only appears when you have at least one completed build.'] },
    { id: 'bm-build-date', target: 'bm-build-date', section: 'recent', conditional: true,
      shortTitle: 'When It Was Made',
      text: 'When a recent build was generated.',
      body: ['This shows when a recent Battlemaps build was generated.',
             'It helps you find your most recent work quickly.',
             'It only appears when you have at least one completed build.'] },
    { id: 'bm-build-open', target: 'bm-build-open', section: 'recent', conditional: true,
      shortTitle: 'Open in Workspace',
      text: 'Opens this build in your workspace.',
      body: ['This opens the selected build in your workspace so you can continue working with it.',
             'Use it to pick up a specific recent build.',
             'It only appears when you have at least one completed build.'] },
  ];

  const quicklinks = [
    { id: 'bm-quicklinks-card', target: 'bm-quicklinks-card', section: 'quicklinks',
      shortTitle: 'Quick Links',
      text: 'Fast shortcuts to the Battlemaps places you use most.',
      body: ['This panel is a set of one-click shortcuts to the Battlemaps areas you use most.',
             'Use it to jump straight to packs, libraries, the workspace, pricing and your account.',
             'The next steps explain exactly where each shortcut takes you.'] },
    { id: 'bm-ql-packs', target: 'bm-ql-packs', section: 'quicklinks',
      shortTitle: 'Environment Packs',
      text: 'Browse and manage your environment packs.',
      body: ['A shortcut to the Environment Packs area.',
             'Use it to browse and manage the environments available across your Battlemaps systems.',
             'Clicking it opens the packs page.'] },
    { id: 'bm-ql-library', target: 'bm-ql-library', section: 'quicklinks',
      shortTitle: 'Map Libraries',
      text: 'Browse the Battlemaps generator libraries.',
      body: ['A shortcut into the Battlemaps generator libraries.',
             'Use it to browse tactical maps, terrain, structures and props.',
             'Clicking it opens the library filtered to Battlemaps.'] },
    { id: 'bm-ql-engines', target: 'bm-ql-engines', section: 'quicklinks',
      shortTitle: 'Creation Systems',
      text: 'Opens the five Battlemaps creation systems.',
      body: ['A shortcut to the Battlemaps creation systems.',
             'Use it to reach all five build engines in one place.',
             'Clicking it opens the creation systems page.'] },
    { id: 'bm-ql-workspace', target: 'bm-ql-workspace', section: 'quicklinks',
      shortTitle: 'My Builds',
      text: 'Opens your saved Battlemaps workspace.',
      body: ['A shortcut to your saved Battlemaps builds in the workspace.',
             'Use it to pick up earlier work.',
             'Clicking it opens the workspace.'] },
    { id: 'bm-ql-pricing', target: 'bm-ql-pricing', section: 'quicklinks',
      shortTitle: 'Pricing & Plans',
      text: 'Opens Battlemaps pricing, credits and packs.',
      body: ['A shortcut to Battlemaps pricing.',
             'Use it to review credits, packs and subscription options.',
             'Clicking it opens the pricing page.'] },
    { id: 'bm-ql-rewards', target: 'bm-ql-rewards', section: 'quicklinks',
      shortTitle: 'Rewards & Referrals',
      text: 'Earn credits by sharing VISINVOKE.',
      body: ['A shortcut to Rewards & Referrals.',
             'Use it to earn credits by inviting others to VISINVOKE.',
             'Clicking it opens the rewards page.'] },
    { id: 'bm-ql-account', target: 'bm-ql-account', section: 'quicklinks',
      shortTitle: 'Account & Billing',
      text: 'Manage your subscription and details.',
      body: ['A shortcut to your account and billing.',
             'Use it to manage your subscription and account details.',
             'Clicking it opens your account area.'] },
    { id: 'bm-ql-password', target: 'bm-ql-password', section: 'quicklinks',
      shortTitle: 'Change Password',
      text: 'Opens the change-password box on this page.',
      body: ['This opens a small box on this page for changing your account password.',
             'Use it to update your password without leaving the dashboard.',
             'It opens right here — you stay on the Battlemaps page.'] },
  ];

  const navigation = [
    ...NAV_TARGETS.map(t => ({ ...t, section: 'navigation' })),
    ...GLOBAL_NAV_TARGETS.map(t => ({ ...t, section: 'navigation' })),
  ];

  // Reading order: header → control centre → packs/credits → creation suite →
  // recent builds → quick links → top navigation (navigation last, like D&D).
  return finalize([
    ...header, ...control, ...account, ...suite, ...recent, ...quicklinks, ...navigation,
  ]);
}

/**
 * Build the ordered manifest for a page ('visual' | 'professional' |
 * 'battlemaps'). Order is assigned in reading order.
 */
function buildSecurityManifest() {
  return finalize([
    { id: 'sec-header-title', target: 'sec-header-title', section: 'overview',
      shortTitle: 'Security / PC Protection',
      text: 'This is where you manage all your protected Windows computers.',
      body: ['This is your VISINVOKE Security page. Every Windows computer you protect is managed here, in one place.',
             'You never deal with the underlying providers; VISINVOKE manages the service for you.'] },
    { id: 'sec-header-badge', target: 'sec-header-badge', section: 'overview',
      shortTitle: 'Where You Are',
      text: 'A badge confirming you are in the Security area.',
      body: ['This badge confirms which VISINVOKE area you are in: Security.', 'It is a label, not a button.'] },
    { id: 'sec-overview', target: 'sec-overview', section: 'overview', conditional: true,
      shortTitle: 'Your Protection At A Glance',
      text: 'How many computers each service protects, and the monthly cost.',
      body: ['These panels show, for each service, how many Windows computers are protected and the monthly cost.',
             'Managed PC Protection and Track, Lock & Wipe are each US$9.99 per active computer, per month.',
             'A computer can have one service or both.'] },
    { id: 'sec-billing-total', target: 'sec-billing-total', section: 'overview', conditional: true,
      shortTitle: 'Monthly Security Total',
      text: 'US$9.99 per active Windows PC per service. No installation fee.',
      body: ['This is your current total monthly Security cost across every protected computer and service.',
             'Billing is US$9.99 per active Windows PC, per service, each month. There is no installation or setup fee.',
             'Adding, replacing or removing computers changes this total through your existing billing, with no new checkout.'] },
    { id: 'sec-add-computer', target: 'sec-add-computer', section: 'setup',
      shortTitle: 'Protect A Computer',
      text: 'Add a Windows computer to protect it, now or any time.',
      body: ['Use this to protect a computer: your first one, or any additional computer later.',
             'You give it a friendly name, choose Desktop or Laptop, pick Managed PC Protection and/or Track, Lock & Wipe, and see the new monthly total before you confirm.',
             'Then you open the installation link on that computer to finish.'] },
    { id: 'sec-endpoint-card', target: 'sec-endpoint-card', section: 'computers', conditional: true,
      shortTitle: 'Each Protected Computer',
      text: 'Every computer shows its services, state, monthly cost and actions.',
      body: ['Each card is one Windows computer, with its friendly name and whether it is a Desktop or Laptop.',
             'It shows which services it has and their state, and its monthly cost.',
             'If a service is Not Active, an Add control lets you add it, for example Add Track, Lock & Wipe to a computer that only has Managed PC Protection.'] },
    { id: 'sec-protection-status', target: 'sec-protection-status', section: 'computers', conditional: true,
      shortTitle: 'Is My Protection Working?',
      text: 'The service state and, where available, Antivirus and Web Protection.',
      body: ['This shows the real state of each service on the computer.',
             'Protected means the managed protection is genuinely active. Other states such as Ready to Install, Installing, Registering, Offline, Needs Attention or Installation Incomplete tell you exactly where a computer is.',
             'A computer is only shown as Protected when the service is actually active.'] },
    { id: 'sec-install', target: 'sec-install', section: 'install', conditional: true,
      shortTitle: 'Install On This Computer',
      text: 'Prepare the installation link for a named computer and run it there.',
      body: ['Installation is prepared for one named computer. Open the link on that computer, or copy or email the link so it can be opened there.',
             'If installation does not complete, the computer stays in an installing or incomplete state; you can prepare a fresh link and try again.',
             'Installation links belong to a single computer and expire for safety; you can regenerate one at any time.'] },
    { id: 'sec-replace', target: 'sec-replace', section: 'lifecycle', conditional: true,
      shortTitle: 'Replace A Computer',
      text: 'Swap in new hardware while keeping your service and history.',
      body: ['Replace creates a new computer that carries the same service as the old one, and keeps the old computer in your history.',
             'It is not a cancellation and repurchase.',
             'When the number of protected computers stays the same, your monthly Security total does not change. The old computer stays protected until the new one is active.'] },
    { id: 'sec-request-removal', target: 'sec-request-removal', section: 'lifecycle', conditional: true,
      shortTitle: 'Request Protection Removal',
      text: 'Start the authorised removal process for a computer.',
      body: ['Your managed protection is secured against unauthorised removal. Requesting removal starts an authorised removal process for that computer.',
             'Protection remains active, and billing continues, until the authorised removal is completed and verified.',
             'Removing Track, Lock & Wipe never triggers Locate, Lock or Wipe; those are separate.'] },
    { id: 'sec-theft', target: 'sec-overview', section: 'theft', conditional: true,
      shortTitle: 'If A Laptop Is Lost Or Stolen',
      text: 'Track, Lock & Wipe helps protect the information on a missing computer.',
      body: ['Track, Lock & Wipe is designed for Windows computers that can be lost or stolen, such as laptops taken away from the business.',
             'In one real case, a customer had a protected laptop in a stolen vehicle. When the laptop later came online the managed service supplied location information, the customer gave it to police, and the vehicle and laptop were recovered.',
             'Recovery is never guaranteed, VISINVOKE itself does not recover property, a location is not always available, and police response is not guaranteed. If a computer is lost or stolen, contact VISINVOKE so the managed service can assist where the device and its connection permit.'] },
    { id: 'sec-c-managed', target: 'sec-header-title', section: 'concepts',
      shortTitle: 'Managed And Monitored',
      text: 'Managed means VISINVOKE sets it up and looks after it; monitored means its health is watched.',
      body: ['Managed means VISINVOKE professionally installs, configures and maintains the protection for you, rather than leaving you to manage security settings yourself.',
             'Monitored means the service health and protection status are watched centrally so problems can be seen.',
             'This is the difference between self-managed consumer security and professionally managed VISINVOKE protection. It is more than an antivirus licence.'] },
    { id: 'sec-c-av', target: 'sec-protection-status', section: 'concepts', conditional: true,
      shortTitle: 'Antivirus And Web Protection',
      text: 'Antivirus stops malicious software; Web Protection blocks dangerous websites.',
      body: ['Antivirus is professional endpoint malware protection that detects and stops malicious software on the computer.',
             'Web Protection helps block access to malicious or dangerous websites.',
             'Both are part of Managed PC Protection and are shown per computer when provider data is available.'] },
    { id: 'sec-c-states', target: 'sec-protection-status', section: 'concepts', conditional: true,
      shortTitle: 'Installation And Protection States',
      text: 'What Ready to Install, Installing, Registering, Activating and Protected mean.',
      body: ['Ready to Install means the installation link is prepared. Installing and Registering mean the software is being set up and enrolled. Activating Protection means protection is coming online.',
             'Protected means the service is genuinely active. Installation Incomplete means installation did not finish; you can prepare a fresh link and try again.',
             'A computer is only shown as Protected when the service is really active.'] },
    { id: 'sec-c-offline', target: 'sec-protection-status', section: 'concepts', conditional: true,
      shortTitle: 'Offline And Needs Attention',
      text: 'What happens when a computer is offline, and when it needs attention.',
      body: ['Offline means the computer has not recently checked in. The protection stays installed, and the status updates again when the computer reconnects.',
             'Needs Attention means something should be looked at, for example a protection module that is not active.',
             'Neither state removes protection or changes your billing on its own.'] },
    { id: 'sec-c-devices', target: 'sec-header-title', section: 'concepts',
      shortTitle: 'Desktop And Laptop',
      text: 'Security supports Windows Desktop and Laptop computers.',
      body: ['Security currently supports Windows Desktop and Windows Laptop computers only.',
             'Track, Lock & Wipe is especially useful for laptops that are regularly taken away from the business.'] },
    { id: 'sec-c-llw', target: 'sec-overview', section: 'concepts', conditional: true,
      shortTitle: 'Locate, Lock And Wipe',
      text: 'What each Track, Lock & Wipe action means, and how they are protected.',
      body: ['Locate helps find a missing computer. Lock restricts access to it. Wipe removes the information on it to protect your data.',
             'These are security-sensitive actions. They are never triggered by billing, cancellation or removal, and they only work where the device and its connection permit.'] },
    { id: 'sec-c-uninstall', target: 'sec-overview', section: 'concepts', conditional: true,
      shortTitle: 'Protected Against Unauthorised Removal',
      text: 'Managed protection resists ordinary removal; removal is an authorised process.',
      body: ['Managed PC Protection is secured against unauthorised removal, so it cannot simply be uninstalled by someone who should not.',
             'When you genuinely want it removed, Request Removal starts an authorised removal process, and protection stays active until that is completed and verified.'] },
    { id: 'sec-c-billing', target: 'sec-billing-total', section: 'concepts', conditional: true,
      shortTitle: 'Per-PC Billing And Service Changes',
      text: 'US$9.99 per active Windows PC per service; changes go through your existing billing.',
      body: ['You pay US$9.99 per active Windows PC, per service, per month. There is no installation or setup fee.',
             'Adding or removing one service changes only that service on that computer, through your existing billing cycle with no new checkout.'] },
    { id: 'sec-c-payment', target: 'sec-billing-total', section: 'concepts', conditional: true,
      shortTitle: 'Payment Problems',
      text: 'A payment problem affects billing only, never your protection.',
      body: ['If there is a payment problem it affects your account and billing only.',
             'It never uninstalls Managed PC Protection, removes Track, Lock & Wipe, or locks or wipes a computer. Billing and security actions are kept separate.'] },
    { id: 'sec-c-privacy', target: 'sec-header-title', section: 'concepts',
      shortTitle: 'What VISINVOKE Can And Cannot See',
      text: 'VISINVOKE sees protection status, not your files or content.',
      body: ['VISINVOKE can see the protection status, health and check-in of the service it manages for you, so it can tell whether your computers are protected.',
             'VISINVOKE does not see your files, documents or the content of what you do on your computer. Track, Lock & Wipe location assistance is available only when the service and the connection permit it.'] },
  ]);
}

export function buildHelpManifest(pageKey) {
  if (pageKey === 'battlemaps') return buildBattlemapsManifest();
  if (pageKey === 'security') return buildSecurityManifest();
  const page = PAGE[pageKey] || PAGE.visual;

  const header = [
    { id: 'header-title', target: 'header-title', section: 'header',
      shortTitle: 'This Page',
      text: `You are on the ${page.productName} page.`,
      body: [`This is the ${page.productName} page — your home base for creating and managing these images.`,
             'Everything you need to start a new image or continue an old one is on this page.'] },
    { id: 'header-badge', target: 'header-badge', section: 'header', conditional: true,
      shortTitle: 'Where You Are',
      text: 'A badge confirming which product area you are in.',
      body: ['This badge simply confirms which VISINVOKE area you are looking at right now.',
             'It is a label, not a button — it helps you stay oriented as you move between areas.'] },
  ];

  const cont = [
    { id: 'continue-card', target: 'continue-card', section: 'continue', conditional: true,
      shortTitle: 'Continue Your Last Build',
      text: 'Pick up your most recent image right where you left off.',
      body: ['This panel appears when you have a recent build. It shows your last image so you can jump straight back in.',
             'Use it when you want to keep working on what you made last, instead of starting fresh.',
             'It only appears when you have a recent build to continue.'] },
    { id: 'continue-thumb', target: 'continue-thumb', section: 'continue', conditional: true,
      shortTitle: 'Last Build Preview',
      text: 'A preview of the last image you generated.',
      body: ['This thumbnail shows the last image you generated, so you can recognise the build at a glance.',
             'It is a preview — the buttons beside it are how you act on it.'] },
    { id: 'continue-primary', target: 'continue-primary', section: 'continue', conditional: true,
      shortTitle: 'Continue / Resume',
      text: 'Reopens your last build in the generator to keep working.',
      body: ['This reopens your last build in the generator with your previous settings loaded.',
             'Use it to keep refining the same image.',
             'It takes you into the generator ready to adjust and regenerate.'] },
    { id: 'continue-workspace', target: 'continue-workspace', section: 'continue', conditional: true,
      shortTitle: 'Open Workspace',
      text: 'Opens all your saved builds, presets and history.',
      body: ['This opens your Workspace — every saved build, preset, template and your build history.',
             'Use it when you want an earlier build rather than the most recent one.'] },
    { id: 'continue-browse', target: 'continue-browse', section: 'continue', conditional: true,
      shortTitle: 'Browse Generators',
      text: 'Opens the library to start something new.',
      body: ['This opens the generator library so you can start a brand-new image.',
             'Use it when you want to create something different rather than continue your last build.'] },
  ];

  const plan = [
    { id: 'plan-card', target: 'plan-card', section: 'plan',
      shortTitle: 'Generator Plan',
      text: 'Your current membership for these generators.',
      body: ['This card shows your current membership level for the generators.',
             'Use it to check what you have and to manage or change it.',
             'Your plan decides how many credits you get and what you can access.'] },
    { id: 'plan-name', target: 'plan-name', section: 'plan',
      shortTitle: 'Your Plan Name',
      text: 'The name of the plan you are currently on.',
      body: ['This is the name of your current plan (for example, Architect).',
             'Each plan name is simply a tier — higher tiers include more monthly credits.',
             'It is shown so you always know which tier you are on.'] },
    { id: 'plan-status', target: 'plan-status', section: 'plan',
      shortTitle: 'Plan Status',
      text: '“Active” means your plan is live and ready to use.',
      body: ['This shows the status of your plan. “Active” means it is live and working.',
             'A “Trial” status means you are on a one-time trial with a limited number of credits.',
             'It is here so you can confirm your access at a glance.'] },
    { id: 'plan-manage', target: 'plan-manage', section: 'plan',
      shortTitle: 'Manage Subscription',
      text: 'Opens billing and plan management.',
      body: ['This opens your subscription management — change plan and view billing history.',
             'Use it when you want to upgrade, downgrade, or check your billing.',
             'It does not change anything on its own; it takes you to where you make those changes.'] },
    { id: 'plan-viewplans', target: 'plan-viewplans', section: 'plan', conditional: true,
      shortTitle: 'View Plans',
      text: 'Compares all available plans and pricing.',
      body: ['This opens the full list of plans and pricing so you can compare them.',
             'Use it when deciding whether to move to a different tier.',
             'It appears while you are on a trial or one-time purchase.'] },
  ];

  const animation = [
    { id: 'anim-credits', target: 'anim-credits', section: 'animation',
      shortTitle: 'Animation Credits',
      text: 'A separate credit type used only for animating images.',
      body: ['Animation Credits are a separate balance used only to animate an image you have generated.',
             'They are different from your monthly creation credits — animating never uses your creation credits, and creating never uses these.',
             'Use this area to see how many animation credits you have.'] },
    { id: 'anim-count', target: 'anim-count', section: 'animation',
      shortTitle: 'Animation Credit Balance',
      text: 'How many animation credits you currently have.',
      body: ['This number is how many Animation Credits you have available right now.',
             'Each animation you make uses some of these credits.',
             'When it runs low, the Buy button tops it up.'] },
    { id: 'anim-buy', target: 'anim-buy', section: 'animation',
      shortTitle: 'Buy Animation Credits',
      text: 'Adds more animation credits to your balance.',
      body: ['This opens the page for buying more Animation Credits.',
             'Use it when you want to animate images and your animation balance is low.',
             'It only affects animation credits — your creation credits are unchanged.'] },
  ];

  const credits = [
    { id: 'credits-card', target: 'credits-card', section: 'credits',
      shortTitle: 'Monthly Credits',
      text: 'How many image-creation credits you have used and have left.',
      body: ['This is your monthly creation-credit meter. Every image you generate uses one credit.',
             'Use it to see how much of your monthly allowance you have spent and how much is left.',
             'On a subscription it refills each billing cycle.'] },
    { id: 'credits-used', target: 'credits-used', section: 'credits',
      shortTitle: 'Credits Used',
      text: 'The number of credits you have used this cycle.',
      body: ['This large number is how many creation credits you have used so far this cycle.',
             'It goes up by one each time you generate an image.',
             'It is shown next to your plan capacity so you can see the balance.'] },
    { id: 'credits-limit', target: 'credits-limit', section: 'credits',
      shortTitle: 'Plan Capacity',
      text: 'The total credits your plan includes each cycle.',
      body: ['This is the total number of creation credits your plan includes in a cycle.',
             'Higher plans include a larger capacity.',
             'Your used figure counts up toward this total.'] },
    { id: 'credits-remaining', target: 'credits-remaining', section: 'credits',
      shortTitle: 'Credits Remaining',
      text: 'How many credits you have left before the next reset.',
      body: ['This shows how many creation credits you have left right now.',
             'Use it to decide whether you can start a large run of images.',
             'When it reaches zero you top up or upgrade to keep creating.'] },
    { id: 'credits-reset', target: 'credits-reset', section: 'credits',
      shortTitle: 'Reset / Renewal',
      text: 'When your credits refill, or how your allowance works.',
      body: ['This line explains when your credits renew, or how your current allowance works.',
             'Subscriptions refill each cycle; a trial or one-time purchase does not reset.',
             'It is here so you know when more credits arrive.'] },
    { id: 'bonus-credits', target: 'bonus-credits', section: 'credits', conditional: true,
      shortTitle: 'Bonus Credits',
      text: 'Extra credits used only after your plan credits run out.',
      body: ['Bonus Credits are extra credits added to your account, for example from a top-up.',
             'They are used automatically only after your monthly plan credits are exhausted.',
             'This panel appears when you have bonus credits available.'] },
  ];

  const library = [
    { id: 'library-card', target: 'library-card', section: 'library',
      shortTitle: page.libraryTitle,
      text: 'Start here when you want to create something new.',
      body: [`This is the ${page.libraryTitle}. It is where you begin a new image.`,
             'Use it to choose a generator and open its creation options.',
             'Browsing the library never changes your existing builds.'] },
    ...page.pills.map((p) => ({
      id: p.id, target: p.id, section: 'library',
      shortTitle: p.shortTitle, text: p.text, modalTitle: p.shortTitle, body: p.body,
    })),
    { id: 'library-browse', target: 'library-browse', section: 'library',
      shortTitle: page.browseLabel,
      text: 'Opens the full library to start creating.',
      body: [`This opens the ${page.libraryTitle} so you can pick a generator.`,
             'Use it to begin a new image.',
             'It takes you into the library; your current work is untouched.'] },
  ];

  const recent = [
    { id: 'recent-card', target: 'recent-card', section: 'recent',
      shortTitle: 'Recent Builds',
      text: 'Your latest generated images, ready to continue.',
      body: ['This section shows your most recent generated images.',
             'Use it to jump back into a build without searching your workspace.',
             'Each card gives you ways to continue or refine that image.'] },
    { id: 'recent-workspace', target: 'recent-workspace', section: 'recent',
      shortTitle: 'Open Workspace',
      text: 'Opens your full workspace of saved builds.',
      body: ['This opens your full Workspace — all saved builds, presets and history.',
             'Use it when the build you want is not among the most recent few.'] },
    { id: 'build-image', target: 'build-image', section: 'recent',
      shortTitle: 'Build Preview',
      text: 'A preview of this generated image.',
      body: ['This is a preview of the generated image for this build.',
             'It helps you recognise the build before you act on it.'] },
    { id: 'build-name', target: 'build-name', section: 'recent',
      shortTitle: 'Build Name',
      text: 'The generator this build came from.',
      body: ['This is the name of the generator this build was made with.',
             'It helps you tell your builds apart at a glance.'] },
    { id: 'build-date', target: 'build-date', section: 'recent',
      shortTitle: 'When It Was Made',
      text: 'How long ago this build was generated.',
      body: ['This shows how long ago the build was created (for example, “2h ago”).',
             'It helps you find the most recent work quickly.'] },
    { id: 'build-status', target: 'build-status', section: 'recent',
      shortTitle: 'Build Clarity',
      text: 'A quick quality signal for how clear this build is.',
      body: ['This coloured tag is a quick clarity signal for the build — a simple read on how clear and well-defined it came out.',
             'Use it as a hint about whether a build might benefit from refining.',
             'It is guidance only; you decide what to do next.'] },
    { id: 'build-continue', target: 'build-continue', section: 'recent',
      shortTitle: 'Continue Build',
      text: 'Reopens this exact build in the generator.',
      body: ['This reopens this exact build in the generator with its settings loaded.',
             'Use it to keep refining this image.',
             'Your saved build stays as it is until you generate again.'] },
    ...MENU_TARGETS,
  ];

  const quicklinks = [
    { id: 'quicklinks-card', target: 'quicklinks-card', section: 'quicklinks',
      shortTitle: 'Quick Links',
      text: 'Fast shortcuts to the places you use most.',
      body: ['This panel is a set of one-click shortcuts to common places.',
             'Use it to jump straight to plans, the workspace, rewards and more.',
             'The next steps explain exactly where each shortcut takes you.'] },
    ...QUICKLINK_TARGETS(page).map(t => ({ ...t, section: 'quicklinks' })),
  ];

  const navigation = [
    ...NAV_TARGETS.map(t => ({ ...t, section: 'navigation' })),
    ...GLOBAL_NAV_TARGETS.map(t => ({ ...t, section: 'navigation' })),
  ];

  return finalize([
    ...header, ...cont, ...plan, ...animation, ...credits,
    ...library, ...recent, ...quicklinks, ...navigation,
  ]);
}

// Topics for the Help chooser (second entry point). Derived from SECTIONS so
// the two entry points always share one manifest.
export function buildHelpTopics(pageKey) {
  const manifest = buildHelpManifest(pageKey);
  return sectionsFor(pageKey)
    .map(sec => {
      const first = manifest.find(s => s.section === sec.key);
      return first ? { key: sec.key, label: sec.label, startId: first.id } : null;
    })
    .filter(Boolean);
}

/**
 * Single source of truth for WHICH Dashboard product families use the guided
 * help system. Dashboard.js gates the HelpEntry mount on this, so the mount
 * condition is testable and a regression (e.g. the wrong key) is caught by a
 * unit test instead of only surfacing on the live site.
 */
export function usesGuidedHelp(pageKey) {
  return pageKey === 'visual' || pageKey === 'professional' || pageKey === 'battlemaps' || pageKey === 'security';
}

const helpManifestApi = { buildHelpManifest, buildHelpTopics, usesGuidedHelp, SECTIONS, BATTLEMAPS_SECTIONS, sectionsFor };
export default helpManifestApi;
